import { DriveStore, GlobalReducer } from "../store/reducers";

type StateSelectorFunction<Selected> = (state: GlobalReducer) => Selected;
type OnChangeFunction<Selected> = (state: Selected) => void;

/**
 * Interally stores previous state of a particular section of the store
 * you are interested in. Will compare that on each even dispatch, and
 * only notify the subscriber when value changes.
 *
 * Potential problems:
 *  - Memory allocation of the `currentState` values in multiple subscriptions
 *  - Performance. On every `dispatch`, it will execute `handleChange` for each
 *    of the subscriptions. This could get costly.
 *
 * Future alternatives: https://github.com/colonelchlorine/redux-subscribe
 * Problem with this alternative is it uses string based path maps to decide
 * which subscribe to notify. This could ugly and bug prone.
 */
export let observeStore = <Selected = unknown>(store: DriveStore, stateSelector: StateSelectorFunction<Selected>, onChange: OnChangeFunction<Selected>) => {
    // When starting observation again, prefill the cached value with current state
    let currentState = stateSelector(store.getState());

    let handleChange = () => {
        let nextState = stateSelector(store.getState());

        if (nextState !== currentState && JSON.stringify(nextState) !== JSON.stringify(currentState)) {
            currentState = nextState;
            onChange(currentState);
        }
    };

    return store.subscribe(handleChange);
};